import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { Reveal, SectionHeading } from "./Reveal";
import { WHY_LENDX } from "../../lib/data";

export const WhyLendXSection = () => {
  return (
    <section id="why-lendx" className="bg-fog py-24 md:py-32" data-testid="why-lendx-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading
          overline="Why Choose LendX"
          title="Finance Is More Than Just a Loan. It's a Strategy."
          testId="why-heading"
        />
        <div className="mt-16 space-y-0 border-t border-gray-200">
          {WHY_LENDX.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.05} y={28}>
              <div
                className="group relative grid items-center gap-4 border-b border-gray-200 py-10 transition-colors duration-500 hover:bg-white md:grid-cols-12 md:gap-8 md:py-12"
                data-testid={`why-item-${i + 1}`}
              >
                <span className="pointer-events-none absolute -top-6 right-0 select-none font-heading text-[110px] font-bold leading-none text-[#D4AF37]/15 transition-colors duration-500 group-hover:text-[#D4AF37]/30 md:text-[150px]">
                  0{i + 1}
                </span>
                <div className="md:col-span-2">
                  <span className="font-mono text-sm text-gold-deep">/ 0{i + 1}</span>
                </div>
                <h3 className="relative font-heading text-2xl font-semibold tracking-tight text-navy md:col-span-4 md:text-3xl">
                  {item.title}
                </h3>
                <p className="relative max-w-md text-sm leading-relaxed text-gray-600 md:col-span-6 md:text-base">
                  {item.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FundingJourney = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 75%", "end 55%"] });
  const steps = [
    { title: "Tell Us Your Requirement", text: "Share your funding requirement and financial objective." },
    { title: "Financial Assessment", text: "We assess your requirement and understand your financing needs." },
    { title: "Solution Structuring", text: "We help identify suitable financing approaches and options." },
    { title: "Documentation Support", text: "Guidance through the application and documentation process." },
    { title: "Funding Coordination", text: "Professional coordination and support throughout the financing process." },
  ];

  return (
    <section id="journey" className="bg-white py-24 md:py-32" data-testid="journey-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading
          overline="How It Works"
          title="The LendX Funding Journey"
          sub="A clear, five-step path from requirement to funding coordination."
          testId="journey-heading"
        />
        <div ref={ref} className="relative mx-auto mt-20 max-w-4xl">
          <div className="absolute inset-y-0 left-4 w-px bg-gray-200 md:left-1/2" />
          <motion.div
            style={{ scaleY: scrollYProgress }}
            className="absolute inset-y-0 left-4 w-px origin-top bg-gold shadow-[0_0_12px_rgba(212,175,55,0.5)] md:left-1/2"
          />
          <div className="space-y-14 md:space-y-20">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={0.05} y={30}>
                <div
                  className={`relative pl-14 md:w-1/2 md:pl-0 ${i % 2 === 0 ? "md:pr-16 md:text-right" : "md:ml-auto md:pl-16"}`}
                  data-testid={`journey-step-${i + 1}`}
                >
                  <span
                    className={`absolute left-4 top-1 flex h-4 w-4 -translate-x-1/2 items-center justify-center md:top-1 ${
                      i % 2 === 0 ? "md:left-full" : "md:left-0"
                    }`}
                  >
                    <span className="absolute h-4 w-4 rounded-full border border-gold/60" />
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                  </span>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold-deep">Step {i + 1}</p>
                  <h3 className="mt-2 font-heading text-xl font-semibold tracking-tight text-navy md:text-2xl">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
